import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import TwoFactorPage from "@/pages/TwoFactorPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import EmployeesPage from "@/pages/EmployeesPage";
import TimeTrackingPage from "@/pages/TimeTrackingPage";
import QuickHoursPage from "@/pages/QuickHoursPage";
import InOutPage from "@/pages/InOutPage";
import SickLeavePage from "@/pages/SickLeavePage";
import WeeklyHoursPage from "@/pages/WeeklyHoursPage";
import OvertimePage from "@/pages/OvertimePage";
import HolidaysPage from "@/pages/HolidaysPage";
import ReportsPage from "@/pages/ReportsPage";
import SettingsPage from "@/pages/SettingsPage";
import UsersPage from "@/pages/UsersPage";
import AuditLogPage from "@/pages/AuditLogPage";
import MyHoursPage from "@/pages/MyHoursPage";
import ProfilePage from "@/pages/ProfilePage";
import PensionRulesPage from "@/pages/PensionRulesPage";
import PensionAdjustmentsPage from "@/pages/PensionAdjustmentsPage";
import InsurancePage from "@/pages/InsurancePage";
import VerificationPage from "@/pages/VerificationPage";
import ManagerAssignmentsPage from "@/pages/ManagerAssignmentsPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import PensionCheckPage from "@/pages/PensionCheckPage";
import "@/App.css";

const Protected = ({ children, minRole, allowDuringPasswordChange = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground" data-testid="loading-screen">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Force first-time password change — but allow the /change-password page itself to render
  if (user.must_change_password && !allowDuringPasswordChange) return <Navigate to="/change-password" replace />;
  const order = { employee: 20, manager: 40, hr_user: 60, admin: 80, super_admin: 100 };
  if (minRole && (order[user.role] || 0) < (order[minRole] || 0)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />} />
      <Route path="/reset-password" element={user ? <Navigate to="/dashboard" replace /> : <ResetPasswordPage />} />
      <Route path="/two-factor" element={<TwoFactorPage />} />
      <Route path="/change-password" element={
        <Protected allowDuringPasswordChange>
          <ChangePasswordPage />
        </Protected>
      } />

      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/employees" element={<Protected minRole="manager"><EmployeesPage /></Protected>} />
        <Route path="/time-tracking" element={<Protected minRole="manager"><TimeTrackingPage /></Protected>} />
        <Route path="/quick-hours" element={<Protected minRole="manager"><QuickHoursPage /></Protected>} />
        <Route path="/in-out" element={<Protected minRole="manager"><InOutPage /></Protected>} />
        <Route path="/weekly-hours" element={<Protected minRole="manager"><WeeklyHoursPage /></Protected>} />
        <Route path="/overtime" element={<Protected minRole="manager"><OvertimePage /></Protected>} />
        <Route path="/holidays" element={<Protected minRole="manager"><HolidaysPage /></Protected>} />
        <Route path="/sick-leave" element={<Protected minRole="manager"><SickLeavePage /></Protected>} />
        <Route path="/reports" element={<Protected minRole="manager"><ReportsPage /></Protected>} />
        <Route path="/users" element={<Protected minRole="admin"><UsersPage /></Protected>} />
        <Route path="/manager-assignments" element={<Protected minRole="admin"><ManagerAssignmentsPage /></Protected>} />
        <Route path="/pension-check" element={<Protected minRole="admin"><PensionCheckPage /></Protected>} />
        <Route path="/pension-rules" element={<Protected minRole="admin"><PensionRulesPage /></Protected>} />
        <Route path="/pension-adjustments" element={<Protected minRole="admin"><PensionAdjustmentsPage /></Protected>} />
        <Route path="/insurance" element={<Protected minRole="admin"><InsurancePage /></Protected>} />
        <Route path="/verify" element={<Protected minRole="hr_user"><VerificationPage /></Protected>} />
        <Route path="/settings" element={<Protected minRole="admin"><SettingsPage /></Protected>} />
        <Route path="/audit" element={<Protected minRole="admin"><AuditLogPage /></Protected>} />
        <Route path="/my-hours" element={<MyHoursPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <AppRoutes />
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}
